<template>
  <div class="container-responsive" style="padding-top: 1rem; padding-bottom: 1rem;">
    <!-- Notification Dialog -->
    <NotificationDialog
      v-model:visible="showNotification"
      :type="notificationType"
      :title="notificationTitle"
      :message="notificationMessage"
      @close="handleNotificationClose"
    />

    <v-card class="elevation-3" style="border-radius: 17px;">
      <v-card-title class="text-center" style="background-color: #002060; color: white; border-radius: 17px 17px 0 0;">
        <v-icon left>mdi-account-edit</v-icon>
        Edit User
      </v-card-title>

      <v-card-text class="pa-6">
        <!-- Top Navigation Bar -->
        <div class="d-flex justify-end mb-4">
          <v-btn
            color="#002060"
            dark
            rounded
            size="large"
            @click="updateUser"
            class="mr-2"
            :disabled="!valid || loading"
          >
            <v-icon left>mdi-content-save</v-icon>
            Save Changes
          </v-btn>
          <v-btn
            color="grey"
            dark
            rounded
            size="large"
            @click="$router.back()"
          >
            <v-icon left>mdi-arrow-left</v-icon>
            Back
          </v-btn>
        </div>

        <!-- User Header -->
        <div class="text-center mb-6">
          <v-avatar size="80" color="#002060" class="mb-3">
            <v-icon size="40" color="white">mdi-account-circle</v-icon>
          </v-avatar>
          <h2 class="user-name mb-2">{{ editUser.firstName }} {{ editUser.surname }}</h2>
          <p class="user-email mb-3">{{ editUser.email }}</p>
          <v-chip 
            :color="getRoleColor(editUser.role)" 
            size="large"
            class="role-chip"
          >
            {{ editUser.role }}
          </v-chip>
        </div>

        <v-form ref="form" v-model="valid" lazy-validation>
          <!-- User Details Sections -->
          <v-row>
            <!-- Personal Information -->
            <v-col cols="12">
              <h3 class="section-title mb-4">Personal Information</h3>
            </v-col>
            
            <v-col cols="12" md="6">
              <v-text-field
                v-model="editUser.firstName"
                label="First Name"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-account"
                :rules="[v => !!v || 'First name is required']"
              />
            </v-col>
            
            <v-col cols="12" md="6">
              <v-text-field
                v-model="editUser.surname"
                label="Surname"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-account"
                :rules="[v => !!v || 'Surname is required']"
              />
            </v-col>
            
            <v-col cols="12" md="6">
              <v-text-field
                v-model="editUser.email"
                label="Email"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-email"
                :rules="[
                  v => !!v || 'Email is required',
                  v => /.+@.+\..+/.test(v) || 'Must be a valid email'
                ]"
                disabled
              />
            </v-col>
            
            <v-col cols="12" md="6">
              <v-text-field
                v-model="editUser.employeeId"
                label="Employee ID"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-card-account-details"
                readonly
              />
            </v-col>
            
            <v-col cols="12" md="6">
              <v-text-field
                v-model="editUser.phone"
                label="Phone"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-phone"
                :rules="phoneValidationRules"
                @input="formatPhoneInput"
                placeholder="074 551 1155"
              />
            </v-col>

            <v-col cols="12" md="6">
              <v-select
                v-model="editUser.role"
                :items="userRoles"
                label="Role"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-account-tie"
                :rules="[v => !!v || 'Role is required']"
              />
            </v-col>

            <v-col cols="12" md="6">
              <v-select
                v-model="editUser.department"
                :items="departments"
                label="Department"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-office-building"
              />
            </v-col>

            <v-col cols="12" md="6">
              <v-select
                v-model="editUser.status"
                :items="statusOptions"
                label="Status"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-account-check"
                :rules="[v => !!v || 'Status is required']"
              />
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { db } from '../firebaseConfig';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { ActivityLogger } from '../utils/activityLogger';
import { mapGetters } from 'vuex';
import NotificationDialog from '../components/NotificationDialog.vue';
import { formatPhoneInput, getPhoneValidationRules } from '../utils/phoneFormatter';

export default {
  name: 'EditUser',
  components: {
    NotificationDialog
  },
  computed: {
    ...mapGetters(['user']),
    phoneValidationRules() {
      return getPhoneValidationRules();
    }
  },
  data() {
    return {
      valid: false,
      loading: false,
      showNotification: false,
      notificationType: 'success',
      notificationTitle: '',
      notificationMessage: '',
      editUser: {
        firstName: '',
        surname: '',
        email: '',
        phone: '',
        employeeId: '',
        role: '',
        department: '',
        status: 'active'
      },
      userRoles: ['Admin', 'Executive', 'Employee', 'QA Agent'],
      departments: ['Legal', 'HR', 'Finance', 'IT', 'Operations'],
      statusOptions: [
        { title: 'Active', value: 'active' },
        { title: 'Deactivated', value: 'inactive' },
        { title: 'Suspended', value: 'suspended' }
      ]
    };
  },
  
  async created() {
    await this.loadUser();
  },
  
  methods: {
    formatPhoneInput() {
      this.editUser.phone = formatPhoneInput(this.editUser.phone);
    },

    async loadUser() {
      this.loading = true;
      try {
        const userId = this.$route.params.id;
        
        // Try users collection first
        let userDoc = await getDoc(doc(db, 'users', userId));
        
        if (!userDoc.exists()) {
          // If not found in users, try members collection
          userDoc = await getDoc(doc(db, 'members', userId));
        }
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          this.editUser = {
            firstName: userData.firstName || '',
            surname: userData.surname || '',
            email: userData.email || '',
            phone: userData.phone || '',
            employeeId: userData.employeeId || '',
            role: userData.role || '',
            department: userData.department || '',
            status: userData.status || 'active'
          };
        } else {
          // Show error notification
          this.notificationType = 'error';
          this.notificationTitle = 'Error!';
          this.notificationMessage = 'User not found.';
          this.showNotification = true;
        }
      } catch (error) {
        console.error('Error loading user:', error);
        
        // Show error notification
        this.notificationType = 'error';
        this.notificationTitle = 'Error!';
        this.notificationMessage = 'Failed to load user data.';
        this.showNotification = true;
      } finally {
        this.loading = false;
      }
    },

    async updateUser() {
      if (!this.$refs.form.validate()) return;
      
      this.loading = true;
      this.showNotification = false;
      
      try {
        const userId = this.$route.params.id;
        
        // Update user data object
        const userData = {
          firstName: this.editUser.firstName,
          surname: this.editUser.surname,
          phone: this.editUser.phone || '',
          employeeId: this.editUser.employeeId || '',
          role: this.editUser.role,
          department: this.editUser.department,
          status: this.editUser.status,
          updatedAt: new Date().toISOString()
        };
        
        // Try updating in users collection first
        let userDoc = await getDoc(doc(db, 'users', userId));
        
        if (userDoc.exists()) {
          await updateDoc(doc(db, 'users', userId), userData);
        } else {
          // If not found in users, try members collection
          userDoc = await getDoc(doc(db, 'members', userId));
          if (userDoc.exists()) {
            await updateDoc(doc(db, 'members', userId), userData);
          } else {
            throw new Error('User not found in either collection');
          }
        }
        
        console.log('User updated successfully:', userId);
        
        // Log user update activity
        const updatedBy = this.user?.displayName || this.user?.email?.split('@')[0] || 'Unknown';
        await ActivityLogger.userUpdated(updatedBy, this.user?.uid, `${userData.firstName} ${userData.surname}`);
        
        // Show success notification
        this.notificationType = 'success';
        this.notificationTitle = 'Success!';
        this.notificationMessage = 'User updated successfully!';
        this.showNotification = true;
        
      } catch (error) {
        console.error('Error updating user:', error);
        
        // Show error notification
        this.notificationType = 'error';
        this.notificationTitle = 'Error!';
        this.notificationMessage = 'Failed to update user. Please try again.';
        this.showNotification = true;
      } finally {
        this.loading = false;
      }
    },

    handleNotificationClose() {
      this.showNotification = false;
      if (this.notificationType === 'success') {
        // Go back after successful update
        this.$router.back();
      }
    },

    getRoleColor(role) {
      switch (role) {
        case 'Admin':
          return 'primary';
        case 'Executive':
          return 'warning';
        case 'Employee':
          return 'info';
        case 'QA Agent':
          return 'success';
        default:
          return 'info';
      }
    }
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;700&display=swap');

* {
  font-family: 'Montserrat', sans-serif;
}

.section-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #002060;
  border-bottom: 2px solid #e5e7eb;
  padding-bottom: 0.5rem;
}

.user-name {
  font-size: 1.75rem;
  font-weight: 700;
  color: #002060;
  margin: 0;
}

.user-email {
  font-size: 1rem;
  color: #666;
  margin: 0;
  font-weight: 500;
}

.role-chip {
  font-weight: 600;
  font-size: 1rem;
}

:deep(.v-field--variant-solo) {
  background-color: #f8f9fa !important;
}

:deep(.v-field--variant-solo .v-field__overlay) {
  background-color: #f8f9fa !important;
}
</style> 